import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaIndustry, FaUsers, FaMedal, FaGlobeEurope } from 'react-icons/fa';
import imageLoader from '../utils/imageLoader';

const AboutPage = () => {
  const { t } = useTranslation();
  return (
    <>
      {/* Hero Section */}
      <section className="bg-primary text-white py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">{t('about.hero.title')}</h1>
          <p className="text-xl max-w-3xl">
            {t('about.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-12 items-start">
            <div className="md:w-1/2">
              <h2 className="text-3xl font-bold mb-6">{t('about.whoWeAre.title')}</h2>
              <p className="text-gray-300 mb-6">
                {t('about.whoWeAre.paragraph1')}
              </p>
              <p className="text-gray-300 mb-6">
                {t('about.whoWeAre.paragraph2')}
              </p>
              <p className="text-gray-300 mb-6">
                {t('about.whoWeAre.paragraph3')}
              </p>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-6 mt-10">
                <div className="bg-dark-light p-6 rounded-lg">
                  <p className="text-4xl font-bold text-white">1964</p>
                  <p className="text-gray-300">{t('about.stats.yearFounded')}</p>
                </div>
                <div className="bg-dark-light p-6 rounded-lg">
                  <p className="text-4xl font-bold text-white">2,000</p>
                  <p className="text-gray-300">{t('about.stats.steelAnnually')}</p>
                </div>
                <div className="bg-dark-light p-6 rounded-lg">
                  <p className="text-4xl font-bold text-white">5,000m²</p>
                  <p className="text-gray-300">{t('about.stats.indoorFactory')}</p>
                </div>
                <div className="bg-dark-light p-6 rounded-lg">
                  <p className="text-4xl font-bold text-white">19,000m²</p>
                  <p className="text-gray-300">{t('about.stats.outdoorArea')}</p>
                </div>
              </div>
            </div>

            <div className="md:w-1/2">
              {/* Factory image */}
              <div className="bg-dark-medium rounded-lg overflow-hidden shadow-lg mb-8">
                <img 
                  src={imageLoader.getAboutFactoryImage() || 'https://via.placeholder.com/800x600?text=Factory+Image'} 
                  alt={t('about.images.factory')} 
                  className="w-full h-96 object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/800x600?text=Factory+Image';
                  }}
                />
              </div>
              {/* Production image */}
              <div className="bg-dark-medium rounded-lg overflow-hidden shadow-lg">
                <img 
                  src={imageLoader.getAboutProductionImage() || 'https://via.placeholder.com/800x400?text=Production+Image'} 
                  alt={t('about.images.production')} 
                  className="w-full h-60 object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/800x400?text=Production+Image';
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-dark-medium">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">{t('about.values.title')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Value 1 */}
            <div className="bg-dark-light p-6 rounded-lg shadow-md text-center">
              <div className="bg-primary/10 inline-flex p-4 rounded-full mb-6">
                <FaIndustry className="text-primary text-3xl" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-white">{t('about.values.value1.title')}</h3>
              <p className="text-gray-300">
                {t('about.values.value1.description')}
              </p>
            </div>
            
            {/* Value 2 */}
            <div className="bg-dark-light p-6 rounded-lg shadow-md text-center">
              <div className="bg-primary/10 inline-flex p-4 rounded-full mb-6">
                <FaUsers className="text-primary text-3xl" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-white">{t('about.values.value2.title')}</h3>
              <p className="text-gray-300">
                {t('about.values.value2.description')}
              </p>
            </div>
            
            {/* Value 3 */}
            <div className="bg-dark-light p-6 rounded-lg shadow-md text-center">
              <div className="bg-primary/10 inline-flex p-4 rounded-full mb-6">
                <FaMedal className="text-primary text-3xl" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-white">{t('about.values.value3.title')}</h3>
              <p className="text-gray-300">
                {t('about.values.value3.description')}
              </p>
            </div>
            
            {/* Value 4 */}
            <div className="bg-dark-light p-6 rounded-lg shadow-md text-center">
              <div className="bg-primary/10 inline-flex p-4 rounded-full mb-6">
                <FaGlobeEurope className="text-primary text-3xl" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-white">{t('about.values.value4.title')}</h3>
              <p className="text-gray-300">
                {t('about.values.value4.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Mission */}
            <div className="lg:w-1/2 bg-dark-light p-8 rounded-lg shadow-md">
              <h3 className="text-2xl font-bold mb-4 text-white">{t('about.mission.title')}</h3>
              <p className="text-gray-300 mb-6">
                {t('about.mission.paragraph1')}
              </p>
              <p className="text-gray-300">
                {t('about.mission.paragraph2')}
              </p>
            </div>
            
            {/* Vision */}
            <div className="lg:w-1/2 bg-dark-light p-8 rounded-lg shadow-md">
              <h3 className="text-2xl font-bold mb-4 text-white">{t('about.vision.title')}</h3>
              <p className="text-gray-300 mb-6">
                {t('about.vision.paragraph1')}
              </p>
              <p className="text-gray-300">
                {t('about.vision.paragraph2')}
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
